import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";

const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Disclaimer = lazy(() => import("./pages/Disclaimer.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));

const queryClient = new QueryClient();

const DeferredCookieConsent = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    const schedule = w.requestIdleCallback
      ? () => w.requestIdleCallback!(() => setShow(true), { timeout: 3000 })
      : () => window.setTimeout(() => setShow(true), 2000);
    const id = schedule();
    return () => {
      if (typeof id === "number") clearTimeout(id);
    };
  }, []);
  if (!show) return null;
  return <CookieConsent />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route
              path="/story"
              element={
                <Index
                  seo={{
                    path: "/story",
                    title: "My story — Eliza Reed Wellness",
                    description:
                      "Eliza's honest story about body changes after 45 — and the quiet shift that helped her feel like herself again.",
                    scrollTo: "story",
                  }}
                />
              }
            />
            <Route
              path="/education"
              element={
                <Index
                  seo={{
                    path: "/education",
                    title: "What I learned about the body after 45 — Eliza Reed Wellness",
                    description:
                      "What changes in a woman's body after 45 — hormones, sleep, stress — explained gently, in plain words.",
                    scrollTo: "education",
                  }}
                />
              }
            />
            <Route
              path="/solution"
              element={
                <Index
                  seo={{
                    path: "/solution",
                    title: "A gentle approach for women 45+ — Eliza Reed Wellness",
                    description:
                      "A gentle approach to feeling like yourself again after 45 — walking, real food, better sleep, less stress. No strict diets.",
                    scrollTo: "solution",
                  }}
                />
              }
            />
            <Route
              path="/guide"
              element={
                <Index
                  seo={{
                    path: "/guide",
                    title: "Free 7-Day Belly Reset Guide for Women 45+ — Eliza Reed Wellness",
                    description:
                      "Get the free 7-day Gentle Reset guide for women 45+ — a calm, doable plan with simple meals, walks, and a printable checklist.",
                    scrollTo: "guide",
                  }}
                />
              }
            />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <DeferredCookieConsent />
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
